import {getRandomColor} from "../../../utils/GetRandomColor";
import {MathJax, MathJaxContext} from "better-react-mathjax";

import bernoulli from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/bernoulli.png";
import chisquared from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/chisquared.png";
import exponential from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/exponential.png";
import logistic from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/logistic.png";
import normal from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/normal.png";
import poisson from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/poisson.png";
import studentst from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/studentst.png";
import uniform from "../../../assets/img/blog/mathematicalArtificialIntelligence/distributions/uniform.png";

export default function ProbabilityTheory() {
    const imgStyle = {
        backgroundColor: "#E9E8E6",
        border: `5px solid ${getRandomColor()}`,
    }

    const altTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        textAlign: "center",
        marginTop: "-30px",
        marginBottom: "20px",
        fontStyle: "italic",
    }

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className={"blog-body"}>
                <h2>Probability Theory</h2>
                <p>
                    By observing nature, patterns can be identified, and probability methods can be used to
                    make predictions about the future based on these patterns. Because machine learning algorithms
                    are
                    trained with observations to compute probabilities, this area of mathematics
                    takes a critical role in artificial intelligence applications. The correctness of predictions
                    made by machines is measured with confidence intervals to see how certain they are
                    (DasGupta, 2011). However, this section consists of
                    Bayes’ theorem, expected value and variance, distributions, and the expected value and
                    variance of these distributions, which form a basis for statistics.
                </p>

                <h3>Bayes' Theorem</h3>
                <p>
                    Bayes' theorem is a mathematical equation used to calculate conditional probability. In
                    1763, Thomas Bayes discovered that the theorem expresses the likelihood of an event that
                    is affected by another event (Bayes' theorem, 2022). If <MathJax
                    inline>{'$A$'}</MathJax> and <MathJax inline>{'$B$'}</MathJax> are the events and, <MathJax
                    inline>{'$P(A)$'}</MathJax> and <MathJax inline>{'$P(B)$'}</MathJax> are the probabilities of these
                    events, Bayes' theorem is as follows.
                </p>

                <MathJax>{'\\begin{align}\nP(A \\mid B)=\\frac{P(B \\mid A) \\cdot P(A)}{P(B)}\n\\end{align}'}</MathJax>

                <p>
                    Where, <MathJax inline>{'$P(A \\mid B)$'}</MathJax> is the probability of <MathJax
                    inline>{'$A$'}</MathJax> being true when <MathJax inline>{'$B$'}</MathJax> is true, and <MathJax
                    inline>{'$P(B \\mid A)$'}</MathJax> is
                    the
                    probability of <MathJax inline>{'$B$'}</MathJax> being true when <MathJax
                    inline>{'$A$'}</MathJax> is true.
                </p>

                <h3>Expected Value and Variance</h3>
                <p>
                    There are two types of variables: Discrete and continuous. Discrete variables can be counted,
                    that
                    is, they are finite. Continuous variables, on the other hand, cannot be counted, so it is only
                    possible to measure them in an interval.<br/><br/>

                    The expected value is the weighted average of the possible values a variable can take.
                    Since <MathJax inline>{'$X$'}</MathJax> is
                    a random variable, the expected value of <MathJax inline>{'$X$'}</MathJax> is denoted by <MathJax
                    inline>{'$E[X]$'}</MathJax>. The probability function of <MathJax
                    inline>{'$X$'}</MathJax> is <MathJax inline>{'$p(x)$'}</MathJax> and the probability density
                    function of <MathJax inline>{'$X$'}</MathJax> is <MathJax inline>{'$f(x)$'}</MathJax>. The formulas
                    for the expected
                    value
                    are given below.
                </p>

                <MathJax>{'\\begin{align}\nE[X]&=\\sum xp(x)\\:\\:\\: \\text{for discrete variables} \\\\\nE[X]&=\\int _{-\\infty }^{\\infty }xf(x)dx\\:\\:\\:\\text{for continuous variables}\n\\end{align}'}</MathJax>

                <p>
                    Some properties of the expected value are:

                    <ul>
                        <li><MathJax inline>{'$E[X]=\\mu, \\quad (\\mu \\: \\text{is the mean of} \\: X)$'}</MathJax>
                        </li>
                        <li><MathJax inline>{'$E[aX+b]=aE[X]+b$'}</MathJax></li>
                        <li><MathJax inline>{'$E[X + Y] = E[X] + E[Y]$'}</MathJax></li>
                        <li><MathJax
                            inline>{'$E[XY] = E[X]E[Y], \\quad \\text{(X and Y are independent variables)}$'}</MathJax>
                        </li>
                    </ul>

                    Another fundamental element of probability is the variance, which is calculated using the expected
                    value.<br/><br/>

                    The variance is a measure of variability, it indicates how wide a distribution is. Since <MathJax
                    inline>{'$X$'}</MathJax> is a
                    random variable, the variance of <MathJax inline>{'$X$'}</MathJax> is given as <MathJax
                    inline>{'$Var(X)$'}</MathJax>. The formulas are
                </p>

                <MathJax>{'\\begin{align}\nVar(X)&=\\sum (x-\\mu)^2p(x) \\quad \\text{for discrete variables} \\\\\nVar(X)&=\\int _{-\\infty}^{\\infty}(x-\\mu)^2f(x)dx \\quad \\text{for continuous variables}\n\\end{align}'}</MathJax>

                <p>
                    Some properties of the variance are

                    <ul>
                        <li><MathJax
                            inline>{'$Var(X)=\\sigma^2, \\quad (\\sigma^2 \\: \\text{is the variance of} \\: X)$'}</MathJax>
                        </li>
                        <li><MathJax inline>{'$Var(aX+b)=a^2Var(X)$'}</MathJax></li>
                    </ul>

                    The variance can also be expressed as <MathJax inline>{'$E[X^2]-E^2[X]$'}</MathJax>. Can be proved
                    using the properties of the
                    expected value.
                </p>

                <MathJax>{'\\begin{align}\nVar(X)&=E[(X-E[X])^2] \\\\\n&=E[X^2-2XE[X]+E^2[X]] \\\\\n&=E[X^2]-E[2XE[X]]+E[E^2[X]] \\\\\n&=E[X^2]-2E[X]E[X]+E[X]^2 \\\\\n&=E[X^2]-E^2[X]\n\\end{align}'}</MathJax>

                <h3>Distributions</h3>
                <p>
                    Functions that demonstrate frequency of values for a variable are called distributions. They are
                    called either discrete or continuous, depending on the variable. It is important to understand
                    which
                    distribution best fits the given data, as various machine learning models are designed to work
                    optimally with different distributions.
                </p>

                <h4>Uniform Distribution</h4>
                <p>
                    In this distribution, each outcome is equally likely. This means that the mean and variance are
                    not
                    interpretable, so it has no predictive power. The expected value of the uniform distribution
                    is <MathJax inline>{'$E[X]=\\frac{a+b}{2}$'}</MathJax> and its variance is <MathJax
                    inline>{'$Var(X)=\\frac{(b-a)^2}{12}$'}</MathJax>.<br/><br/>
                </p><p>
                The Python code below plots the histogram of a uniform distribution. See Figure 13.
            </p>

                <pre><code className="language-python">{`import numpy as np
import pandas as pd
import matplotlib.pyplot as plt
import scipy.stats as stats

x = np.random.uniform(0, 10, 100)  # hundred random values between [0,10)
plt.hist(x)  # plotting
plt.show()`}</code></pre>


                <img className={"blog-img"} style={imgStyle} src={uniform} alt="Uniform Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 13:</b> The uniform distribution histogram.</p></div>

                <h4>Bernoulli Distribution</h4>
                <p>
                    Distributions with two outcomes for a random variable are called Bernoulli distributions. It has
                    one
                    variable <MathJax inline>{'$p$'}</MathJax> that indicates the greater probability. <MathJax
                    inline>{'$k$'}</MathJax> being the possible outcomes, the
                    probability mass function of the Bernoulli distribution is
                </p>

                <MathJax>{'\\begin{align}\nf(k,p) = \\left\\{\n\\begin{array}{ll}\np & if\\; k=1 \\\\\nq=1-p & if\\; k=0\n\\end{array}\n\\right.\n\\end{align}'}</MathJax>

                <p>
                    And it can also be expressed as
                </p>

                <MathJax>{'\\begin{align}\nf(k,p)=p^k(1-p)^{1-k}\n\\end{align}'}</MathJax>

                <p>
                    Expected value of Bernoulli Distribution is <MathJax inline>{'$E[X]=p$'}</MathJax>.
                </p>

                <MathJax>{'\\begin{align}\nE[X]&=\\sum \\:xp\\left(x\\right)\\: \\\\\n&=0.p^0(1-p)^{1-0}+1.p^1(1-p)^{1-1} \\\\\n&=0(1-p)+p \\\\\n&=p\n\\end{align}'}</MathJax>

                <p>
                    And its variance is <MathJax inline>{'$Var(X)=p(1-p)$'}</MathJax>.
                </p>

                <MathJax>{'\\begin{align}\nE[X^2]&=p.1^2+q.0^2 \\\\\nVar(X)&=E[X^2]-E^2[X]=p-p^2=p(1-p)\n\\end{align}'}</MathJax>

                <p>
                    Bernoulli distribution is denoted by <MathJax inline>{'$X\\sim Bern(p)$'}</MathJax>.
                </p>

                <h4>Binomial Distribution</h4>
                <p>
                    To calculate the outcome of more than one Bernoulli trial, the binomial distribution is used.
                    For <MathJax inline>{'$k$'}</MathJax> successful trials in a total of <MathJax
                    inline>{'$n$'}</MathJax> trials, the probability mass function is given below.
                    Note
                    that <MathJax inline>{'$B(1,p)=Bern(p)$'}</MathJax>.
                </p>

                <MathJax>{'\\begin{align}\nf(k,n,p)=\\binom{n}{k}p^k(1-p)^{n-k}\n\\end{align}'}</MathJax>

                <p>
                    Since each variable is an equal Bernoulli random variable with <MathJax
                    inline>{'$E[X]=p$'}</MathJax>, the sum of the n trials
                    simply gives the expected value of the binomial distribution <MathJax inline>{'$E[X]=np$'}</MathJax>.
                    The same is true for the
                    variance, the variance is <MathJax inline>{'$Var(X)=np(1-p)$'}</MathJax>.<br/><br/>
                </p><p>
                    The coin toss is a good example of this distribution. For a coin toss game where heads is
                    defined as
                    0 and tails as 1, the histogram for a total of 5 tosses would look like Figure 14.
                </p>

                    <pre><code className="language-python">{`heads = 1
tails = 0
dataBinomial = np.array([heads, tails, tails, tails, heads])
binomial = pd.DataFrame(dataBinomial)
binomial.plot(kind="hist", legend=False)`}</code></pre>

                <img className={"blog-img"} style={imgStyle} src={bernoulli} alt="Binomial Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 14:</b> The binomial distribution histogram for a coin toss game.</p></div>

                <p>Python function to calculate binomial distribution for k=7, n=10, and p=0.7 is given below.</p>

                <pre><code className="language-python">{`from scipy.special import factorial

def nCr(n, r):
return (
factorial(n) / factorial(r) / factorial(n - r)
)  # defining combination formula

def bernouilliPM(k, n, p):
return nCr(n, k) * (p**k * (1 - p) ** (n - k))  # defining bernoulli pm

# the probability of getting 7 successful results in 10 trials with probability of 0.7
bernouilliPM(7, 10, 0.7)`}</code></pre>

                <p>The output is <code>0.26682793200000005</code>.<br/>
                    Binomial distribution is denoted by <MathJax inline>{'$X\\sim B(n,p)$'}</MathJax>.</p>

                <h4>Poisson Distribution</h4>
                <p>
                    To find out how often a given event will occur at a given time, the Poison distribution is used.
                    With <MathJax inline>{'$k$'}</MathJax> as events, the probability mass function is
                </p>

                <MathJax>{'\\begin{align}\nf(k,\\lambda)=\\frac{\\lambda^ke^{-\\lambda}}{k!}\n\\end{align}'}</MathJax>

                <p>
                    The expected value and variance are both equal to <MathJax inline>{'$\\lambda$'}</MathJax>: <MathJax
                    inline>{'$E[X]=Var(X)=\\lambda$'}</MathJax>.
                </p>

                <MathJax>{'\\begin{align}\nE(X)&=\\sum_{x=0}^{\\infty} x \\frac{e^{-\\lambda} \\lambda^{x}}{x !} \\\\\n&=\\sum_{x=1}^{\\infty} x \\frac{e^{-\\lambda} \\lambda^{x}}{x !}=\\sum_{x=1}^{\\infty}\n\\frac{e^{-\\lambda}\n\\lambda^{x}}{(x-1) !} \\\\\n&=\\lambda e^{-\\lambda} \\sum_{x=1}^{\\infty} \\frac{\\lambda^{x-1}}{(x-1) !} \\\\\n&=\\lambda e^{-\\lambda} \\sum_{x=0}^{\\infty} \\frac{\\lambda^{x}}{x !} \\\\\n&=\\lambda e^{-\\lambda} e^{\\lambda} \\\\\n&=\\lambda\n\\end{align}'}</MathJax>

                <p>
                    For the variance, it is helpful to manipulate the formula <MathJax
                    inline>{'$Var(X)=E\\left(X^{2}\\right)-E(X)^{2}$'}</MathJax> by
                    the following steps.
                </p>

                <MathJax>{'\\begin{align}\n&=E((X)(X-1)+X)-E(X)^{2} \\\\\n&=E((X)(X-1))+E(X)-E(X)^{2} \\\\\n&=E((X)(X-1))+(\\lambda-\\lambda^{2})\n\\end{align}'}</MathJax>

                <p>Replacing,</p>

                <MathJax>{'\\begin{align}\n&=\\sum_{x=0}^{\\infty}(x)(x-1) \\frac{e^{-\\lambda} \\lambda^{x}}{x\n!}+(\\lambda-\\lambda^{2})\n\\\\\n&=\\sum_{x=2}^{\\infty} \\frac{e^{-\\lambda} \\lambda^{x}}{(x-2) !}+(\\lambda-\\lambda^{2})\n\\\\\n&=\\lambda^{2} e^{-\\lambda} \\sum_{x=2}^{\\infty} \\frac{\\lambda^{x-2}}{(x-2)\n!}+(\\lambda-\\lambda^{2}) \\\\\n&=\\lambda^{2} e^{-\\lambda} \\sum_{x=0}^{\\infty} \\frac{\\lambda^{x}}{x\n!}+(\\lambda-\\lambda^{2}) \\\\\n&=\\lambda^{2} e^{-\\lambda} e^{\\lambda}+(\\lambda-\\lambda^{2}) \\\\\n&=\\lambda^{2}+\\lambda-\\lambda^{2} \\\\\n&=\\lambda\n\\end{align}'}</MathJax>

                <p>
                    The diagram of the probability mass function for various <MathJax
                    inline>{'$\\lambda$'}</MathJax> values can be found in Figure
                    15.
                </p>

                    <pre><code className="language-python">{`k = np.arange(0, 10, 1)  # defining the interval
poissonlambda1 = np.exp(-1) * 1**k / factorial(k)
poissonlambda2 = np.exp(-2) * 2**k / factorial(k)
poissonlambda3 = np.exp(-3) * 3**k / factorial(k)
plt.plot(k, poissonlambda1)
plt.plot(k, poissonlambda2)
plt.plot(k, poissonlambda3)
plt.legend(k + 1)  # fixing the legend`}</code></pre>

                <img className={"blog-img"} style={imgStyle} src={poisson} alt="Poisson Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 15:</b> The poisson distribution for <MathJax
                    inline>{'$\\lambda=\\{1,2,3\\}$'}</MathJax>.</p></div>

                <p>Poisson distribution is denoted by <MathJax inline>{'$X\\sim Po(\\lambda)$'}</MathJax>.</p>

                <h4>Normal Distribution</h4>
                <p>
                    Probably the most famous of all distributions, the normal distribution (also known as the
                    Gaussian
                    distribution) occupies a very important place in probability theory and statistics because it is
                    part of the central limit theorem.<br/><br/>

                    The normal distribution is frequently observed in nature. Patterns resulting from biological
                    events
                    often form a normal distribution. The probability density function of the normal distribution is
                </p>

                <MathJax>{'\\begin{align}\nf\\left(x\\right)=\\frac{1}{\\sigma \\sqrt{2\\pi }}e^{-\\frac{1}{2}\\left(\\frac{x-\\mu \\:}{\\sigma\n\\:}\\right)^2}\n\\end{align}'}</MathJax>

                <p>
                    For fixed <MathJax inline>{'$\\mu=0$'}</MathJax> and <MathJax
                    inline>{'$\\sigma^2=\\{0.5,1,2\\}$'}</MathJax> values, the probability density function behaves
                    like
                    in Figure 16.
                </p>

                    <pre><code className="language-python">{`x = np.arange(-5, 5, 0.01)  # setting a range
normalsigmasq05 = (1 / (0.5 * np.sqrt(2 * np.pi))) * np.e ** (
(-1 / 2) * ((x - 0) / 0.5) ** 2
)
normalsigmasq1 = (1 / (1 * np.sqrt(2 * np.pi))) * np.e ** (
(-1 / 2) * ((x - 0) / 1) ** 2
)
normalsigmasq2 = (1 / (2 * np.sqrt(2 * np.pi))) * np.e ** (
(-1 / 2) * ((x - 0) / 2) ** 2
)  # replacing mu with 0 and variance with 0.5,1,2
plt.plot(x, normalsigmasq05)
plt.plot(x, normalsigmasq1)
plt.plot(x, normalsigmasq2)
legend = ("var=0.5", "var=1", "var=2")
plt.legend(labels=legend)`}</code></pre>

                    <img className={"blog-img"} style={imgStyle} src={normal} alt="Normal Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 16:</b> The normal distribution for <MathJax
                    inline>{'$\\mu=0$'}</MathJax> and <MathJax
                    inline>{'$\\sigma^2=\\{0.5,1,2\\}$'}</MathJax>.</p></div>

                <p>
                    The normal distribution is clearly a continuous distribution and the expected value for continuous
                    distributions is calculated with the following formula:
                </p>

                <MathJax>{'\\begin{align}\n\\mathrm{E}[\\mathrm{X}]=\\int_{-\\infty}^{\\infty} x f(x) d x\n\\end{align}'}</MathJax>

                <p>
                    Replacing
                </p>

                <MathJax>{'\\begin{align}\nE\\left(X\\right)&=\\int _{-\\infty }^{\\infty }x\\frac{1}{\\sigma\\sqrt{2\\pi\n}}e^{-\\frac{1}{2}\\left(\\frac{x-\\mu\n}{\\sigma}\\right)^2}dx \\\\\n&=\\frac{1}{\\sigma\\sqrt{2\\pi }}\\int _{-\\infty }^{\\infty }xe^{-\\frac{1}{2}\\left(\\frac{x-\\mu\n}{\\sigma}\\right)^2}dx\n\\end{align}'}</MathJax>

                <p>
                    Changing the variable as <MathJax inline>{'$t=\\frac{x-\\mu}{\\sqrt{2}\\sigma}$'}</MathJax>,
                    gives <MathJax inline>{'$x=\\mu+\\sqrt{2}\\sigma t$'}</MathJax> and <MathJax
                    inline>{'$dx=\\sqrt{2}\\sigma dt$'}</MathJax>. Replacing again,
                </p>

                <MathJax>{'\\begin{align}\nE\\left(X\\right)&=\\frac{1}{\\sigma\\sqrt{2\\pi}}\\int _{-\\infty }^{\\infty}\\left(\\mu +\\sqrt{2}\\sigma t\n\\right)e^{-t^2}\\sqrt{2}\\sigma dt \\\\\n&=\\frac{1}{\\sqrt{\\pi }}\\int _{-\\infty }^{\\infty }\\left(\\mu +\\sqrt{2}\\sigma t \\right)e^{-t^2}dt\n\\end{align}'}</MathJax>

                <p>
                    Splitting the integral into sums and solving them
                </p>

                <MathJax>{'\\begin{align}\n&=\\frac{1}{\\sqrt{\\pi}}\\left[\\mu\\int _{-\\infty }^{\\infty }e^{-t^2}dt+\\sqrt{2}\\sigma\\int\n_{-\\infty}^{\\infty}te^{-t^2}dt\\right] \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[\\mu\\sqrt{\\pi }+\\sqrt{2}\\sigma \\left(-\\frac{1}{2}e^{-t^2}\\right)_{-\\infty\n}^{\\infty }\\right] \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[\\mu \\sqrt{\\pi}+0\\right] \\\\\n&=\\mu\n\\end{align}'}</MathJax>

                <p>
                    The expected value of the normal distribution is <MathJax inline>{'$\\mu$'}</MathJax>.<br/><br/>

                    To find the variance of the normal distribution, we start with <MathJax
                    inline>{'$Var(X)=E[X^2]-E^2[X]$'}</MathJax>. Since
                    <MathJax inline>{'$E[X]=\\mu$'}</MathJax>,
                    the formula becomes <MathJax inline>{'$Var(X)=E[X^2]-\\mu^2$'}</MathJax>.
                </p>

                <MathJax>{'\\begin{align}\nE[X^2]&=\\int _{-\\infty }^{\\infty }x^2\\frac{1}{\\sigma\\sqrt{2\\pi }}e^{\\frac{-\\left(x-\\mu\n\\right)^2}{2\\sigma ^2}}dx-\\mu ^2 \\\\\n&=\\frac{1}{\\sigma \\sqrt{2 \\pi}} \\int_{-\\infty}^{\\infty} x^{2} e^{\\frac{-(x-\\mu)^{2}}{2 \\sigma^{2}}}\nd\nx-\\mu^{2}\n\\end{align}'}</MathJax>

                <p>
                    With the same substitution <MathJax inline>{'$t=\\frac{x-\\mu}{\\sqrt{2}\\sigma}$'}</MathJax>
                </p>

                <MathJax>{'\\begin{align}\n&=\\frac{1}{\\sigma \\sqrt{2 \\pi}} \\int_{-\\infty}^{\\infty}(\\sqrt{2} \\sigma t+\\mu)^{2} e^{-t^{2}}\n\\sqrt{2}\n\\sigma d t-\\mu^{2} \\\\\n&=\\frac{1}{\\sqrt{\\pi}} \\int_{-\\infty}^{\\infty}(\\sqrt{2} \\sigma t+\\mu)^{2} e^{-t^{2}} d t-\\mu^{2} \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[\\int_{-\\infty}^{\\infty}\\left(2 \\sigma^{2} t^{2}+2 \\sqrt{2} \\sigma \\mu\nt+\\mu^{2}\\right) e^{-t^{2}} d t\\right]-\\mu^{2} \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[2 \\sigma^{2} \\int_{-\\infty}^{\\infty} t^{2} e^{-t^{2}} d t+2 \\sqrt{2}\n\\sigma\n\\mu \\int_{-\\infty}^{\\infty} t e^{-t^{2}} d t+\\mu^{2} \\int_{-\\infty}^{\\infty} e^{-t^{2}} d\nt\\right]-\\mu^{2} \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[2 \\sigma^{2} \\int_{-\\infty}^{\\infty} t^{2} e^{-t^{2}} d t+2 \\sqrt{2}\n\\sigma\n\\mu .0+\\mu^{2} \\int_{-\\infty}^{\\infty} e^{-t^{2}} d t\\right]-\\mu^{2} \\\\\n&=\\frac{1}{\\sqrt{\\pi}}\\left[2 \\sigma^{2} \\int_{-\\infty}^{\\infty} t^{2} e^{-t^{2}} d\nt\\right]+\\frac{1}{\\sqrt{\\pi}} \\mu^{2} \\sqrt{\\pi}-\\mu^{2} \\\\\n&=\\frac{2 \\sigma^{2}}{\\sqrt{\\pi}} \\int_{-\\infty}^{\\infty} t^{2} e^{-t^{2}} d t\n\\end{align}'}</MathJax>

                <p>
                    Integrating by parts
                </p>

                <MathJax>{'\\begin{align}\n&=\\frac{2 \\sigma^{2}}{\\sqrt{\\pi}}\\left(\\left[-\\frac{t}{2}\ne^{-t^{2}}\\right]_{-\\infty}^{\\infty}+\\frac{1}{2} \\int_{-\\infty}^{\\infty} e^{-t^{2}} d t\\right) \\\\\n&=\\frac{2 \\sigma^{2}}{\\sqrt{\\pi}}\\left(0+\\frac{1}{2} \\int_{-\\infty}^{\\infty} e^{-t^{2}} d t\\right)\n\\\\\n&=\\frac{\\sigma^{2}}{\\sqrt{\\pi}} \\int_{-\\infty}^{\\infty} e^{-t^{2}} d t \\\\\n&=\\frac{\\sigma^{2}}{\\sqrt{\\pi}} \\sqrt{\\pi} \\\\\n&=\\sigma^{2}\n\\end{align}'}</MathJax>

                <p>
                    As calculated above, variance of the normal distribution is <MathJax
                    inline>{'$\\sigma^2$'}</MathJax>.<br/><br/>

                    Normal distribution is denoted by <MathJax inline>{'$X\\sim N(\\mu,\\sigma^2)$'}</MathJax>.
                </p>

                <h4>Standard Normal Distribution</h4>
                <p>
                    The normal distribution with mean <MathJax inline>{'$\\mu=0$'}</MathJax> and variance <MathJax
                    inline>{'$\\sigma^2=1$'}</MathJax> is called the standard
                    normal
                    distribution.
                    The normal distribution can be standardized with the following formula
                </p>

                <MathJax>{'\\begin{align}\nz=\\frac{x-\\mu}{\\sigma}\n\\end{align}'}</MathJax>

                <p>
                    With this transformation, we obtain the variable <MathJax inline>{'$z$'}</MathJax>, that is, the
                    standard deviations between
                    the
                    values and the mean. Then the z-table is used to find the percentage of variables that are
                    distributed above or below <MathJax inline>{'$x$'}</MathJax>.<br/><br/>

                    The plot of the probability density function of the standard normal distribution can also be
                    seen in
                    Figure 16.<br/><br/>

                    Standard normal distribution is denoted by <MathJax inline>{'$Z\\sim N(0,1)$'}</MathJax>.
                </p>

                <h5>normalization and Standardization</h5>
                <p>
                    For some models to work properly, the data must be normalized or standardized. Both are methods
                    of
                    scaling features that differ from each other in some respects.<br/><br/>

                    Normalization is often used when the distribution of the data is unknown. To determine the
                    normalized value, the minimum and maximum values are needed.
                </p>

                <MathJax>{'\\begin{align}\nX_{\\text {normalized }}=\\frac{X-X_{\\min }}{X_{\\max }-X_{\\min }}\n\\end{align}'}</MathJax>

                <p>
                    Normalized values have a predefined range that lies between <MathJax
                    inline>{'$[-1, 1]$'}</MathJax> or <MathJax inline>{'$[0, 1]$'}</MathJax>, which means
                    that
                    it is affected by outliers. With standardization, on the other hand, this is not the case, since
                    the
                    standard distribution has no defined range.<br/><br/>

                    Standardization is useful when the data are normally distributed. After the following
                    transformation, the mean is set to 0 and the variance is set to 1.
                </p>

                <MathJax>{'\\begin{align}\nz=\\frac{x-\\mu}{\\sigma}\n\\end{align}'}</MathJax>

                <h4>Student's T Distribution</h4>
                <p>
                    Published by William Sealy Gosset under the
                    pseudonym "Student" (because the company he works for did not allow its scientists to
                    use their real names in publications (Raju, 2005)),Student's T distribution can be defined as an
                    approximation to the normal distribution for limited data. This distribution has its own
                    cumulative
                    table, like the z-table used in the standard normal distribution, and it is called the t-table.
                    Student's T distribution is used for small samples with unknown variances, while the standard
                    normal
                    distribution is used for large samples with known variances. <MathJax
                    inline>{'$\\nu$'}</MathJax> in <MathJax inline>{'$X\\sim t(\\nu)$'}</MathJax> represents
                    degrees of freedom, calculated with <MathJax inline>{'$\\nu=n-1$'}</MathJax>, where <MathJax
                    inline>{'$n$'}</MathJax> is the number of observations in the
                    sample. If <MathJax inline>{'$\\nu > 2$'}</MathJax>, expected value <MathJax
                    inline>{'$E[X]=\\mu$'}</MathJax> and <MathJax inline>{'$Var(X)=\\frac{S^2k}{k-2}$'}</MathJax>,
                    where <MathJax inline>{'$S^2$'}</MathJax> is
                    the
                    variance of the sample.<br/><br/>

                    <MathJax inline>{'$\\Gamma$'}</MathJax> is the gamma function, the probability density function of
                    Student's T distribution is
                </p>

                <MathJax>{'\\begin{align}\nf(x, \\nu)=\\frac{\\Gamma((\\nu+1) / 2)}{\\sqrt{\\pi \\nu} \\Gamma(\\nu / 2)}\\left(1+x^{2} /\n\\nu\\right)^{-(\\nu+1) / 2}\n\\end{align}'}</MathJax>

                <p>
                    Below is a Python code to compare Student's T with the standard normal distribution with <MathJax
                    inline>{'$\\nu=1$'}</MathJax>
                    and <MathJax inline>{'$\\nu=2$'}</MathJax>. See Figure 17.
                </p>

                    <pre><code className="language-python">{`x = np.arange(-5, 5, 0.01)  # setting a range
plt.plot(x, stats.norm.pdf(x, 0, 1))
plt.plot(x, stats.t.pdf(x, 1))
plt.plot(x, stats.t.pdf(x, 2))
legend = ("std normal", "stu t, nu=1", "stu t, nu=2")
plt.legend(labels=legend)`}</code></pre>

                    <img className={"blog-img"} style={imgStyle} src={studentst} alt="Student's T Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 17:</b> Student's T with <MathJax
                    inline>{'$\\nu=1, \\: 2$'}</MathJax> and standard
                    normal. Notice the higher tails on
                    Student's T.</p></div>

                <p>
                    One can see that with higher degrees in freedom, Student's T converges to standard normal.<br/><br/>

                    Student's T distribution is denoted by <MathJax inline>{'$X\\sim t(\\nu)$'}</MathJax>.
                </p>

                <h4>Chi-Squared Distribution</h4>
                <p>
                    The chi-square distribution is one of the most commonly used probability distributions, which
                    has
                    little application in real life but plays an important role in hypothesis testing. It is
                    obtained by
                    summing the squares of <MathJax inline>{'$k$'}</MathJax> standard normally distributed random
                    variables, where <MathJax inline>{'$k$'}</MathJax> is the
                    degrees
                    of freedom. The expected value of the chi-squared distribution is <MathJax
                    inline>{'$E[X]=k$'}</MathJax> and the variance is <MathJax inline>{'$Var(X)=2k$'}</MathJax>. The
                    probability density function of this distribution is
                </p>

                <MathJax>{'\\begin{align}\nf(x, k)=\\frac{1}{2^{k / 2} \\Gamma(k / 2)} x^{k / 2-1} e^{(-x / 2)}\n\\end{align}'}</MathJax>

                <p>
                    Here is a graph to compare between different <MathJax inline>{'$k$'}</MathJax> values. As with
                    Student's T, the diagram
                    converges
                    to the normal distribution at higher degrees of freedom. See Figure 18.
                </p>

                    <pre><code className="language-python">{`x = np.arange(0, 10, 0.01)  # setting a range
k = np.arange(1, 8, 1)
legend = []

for df in k:  # plotting k values from 1 to 7
plt.plot(x, stats.chi2.pdf(x, df))
legend.append(f"k={df}")

plt.axis([0, 10, 0, 0.5])  # setting graph limits
plt.legend(labels=legend)`}</code></pre>

                    <img className={"blog-img"} style={imgStyle} src={chisquared} alt="Chi-Squared Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 18:</b> The chi-squared distribution with different degrees of
                    freedom.</p></div>

                <p>
                    The chi-squared distribution is denoted by <MathJax inline>{'$X\\sim \\chi^2(k)$'}</MathJax>.
                </p>

                <h4>Exponential Distribution</h4>
                <p>
                    Like the chi-squared distribution, the exponential distribution is a special case of the gamma
                    distribution. Variables that follow the exponential distribution start with high probability,
                    decrease, and eventually reach a plateau. <MathJax inline>{'$\\lambda$'}</MathJax> in <MathJax
                    inline>{'$X\\sim exp(\\lambda)$'}</MathJax> is defined as rate
                    parameter. It indicates how fast the probability density function reaches the plateau and how
                    spread
                    the graph is. The probability density function is
                </p>

                <MathJax>{'\\begin{align}\nf(x ; \\lambda)=\\left\\{\\begin{array}{ll}\n\\lambda e^{-\\lambda x} & x \\geq 0 \\\\\n0 & x \\lt 0\n\\end{array}\\right.\n\\end{align}'}</MathJax>

                <p>
                    For the distribution graph, see Figure 19.
                </p>

                    <pre><code className="language-python">{`x = np.arange(0, 10, 0.01)
rate = np.arange(0.5, 2.5, 0.5)
legend = []

for lamb in rate:
plt.plot(x, lamb * np.exp(-lamb * x))
legend.append(f"lambda={lamb}")

plt.axis([0, 5, 0, 1.5])
plt.legend(labels=legend)`}</code></pre>

                    <img className={"blog-img"} style={imgStyle} src={exponential} alt="Exponential Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 19:</b> The exponential distribution with different rate
                    parameters.</p></div>

                <p>
                    The exponential distribution is denoted by <MathJax inline>{'$X\\sim exp(\\lambda)$'}</MathJax>.
                </p>

                <h4>Logistic Distribution</h4>
                <p>
                    The logistic distribution is often used to determine how continuous variables affect the
                    probability
                    of Boolean outcomes. <MathJax inline>{'$\\mu$'}</MathJax> in <MathJax
                    inline>{'$X\\sim logistic(\\mu,S)$'}</MathJax> represents location and <MathJax
                    inline>{'$S$'}</MathJax> represents
                    scale.
                    The scale determines how wide the bell curve is.<br/><br/>

                    The probability density function of the logistic distribution
                </p>

                <MathJax>{'\\begin{align}\nf(x ; \\mu, s)=\\frac{e^{-(x-\\mu) / s}}{s\\left(1+e^{-(x-\\mu) / s}\\right)^{2}}\n\\end{align}'}</MathJax>

                <p>
                    is also equal to
                </p>

                <MathJax>{'\\begin{align}\n=\\frac{1}{4 s} \\operatorname{sech}^{2}\\left(\\frac{x-\\mu}{2 s}\\right)\n\\end{align}'}</MathJax>

                <p>
                    The probability density function looks like a normal distribution but has a lower tail. See
                    Figure
                    20.
                </p>

                    <pre><code className="language-python">{`x = np.arange(-10, 20, 0.01)
mu = [2, 9]
s = [2, 3]
legend = []

for location in mu:
for scale in s:
plt.plot(
    x,
    (np.exp(-(x - location) / scale))
    / (scale * (1 + np.exp(-(x - location) / scale)) ** 2),
)
legend.append(f"mu={location},s={scale}")

plt.legend(labels=legend)`}</code></pre>

                    <img className={"blog-img"} style={imgStyle} src={logistic} alt="Logistic Distribution"/><br/>
                <div style={altTextStyle}><p><b>Figure 20:</b> The logistic distribution with different location and
                    scale parameters.</p></div>

                <p>
                    The expected value of the logistic distribution is <MathJax inline>{'$E[X]=\\mu$'}</MathJax> and the
                    variance is <MathJax inline>{'$Var(X)=\\frac{s^2\\pi^2}{3}$'}</MathJax>.<br/><br/>

                    The logistic distribution is denoted by <MathJax inline>{'$X\\sim logistic(\\mu, S)$'}</MathJax>.
                </p>
            </div>
        </MathJaxContext>
    )
}